/**
 * A countdown from an absolute deadline plus the server's clock, since phone clocks are
 * wrong. Renders nothing without a deadline: a zero would read as "time's up".
 */

import { useEffect, useRef, useState } from 'react';

export interface TimerProps {
  /** `core.deadlineAt` — absolute epoch ms, or null when nothing is counting down. */
  endsAt: number | null;
  /** `core.serverNow`, so a phone with a wrong clock still shows the right number. */
  serverNow: number | null;
  /**
   * `stage` fills a TV, `phone` fits a handset, `chip` is a tag beside a title.
   * Sizes, not meanings: which one a surface wants is a layout decision.
   */
  variant?: 'stage' | 'phone' | 'chip';
  /** Seconds at which the clock turns red. */
  lowAt?: number;
  /**
   * Decimal places to show in the final seconds, ticking fast (e.g. `2` → `2.97`). 0 keeps the
   * plain whole-second clock. The decimals only appear once under `preciseUnder` seconds, so a
   * long clock still reads as whole numbers until the finish is near.
   */
  decimals?: number;
  /** Seconds remaining below which the decimal display kicks in. */
  preciseUnder?: number;
}

/**
 * Seconds left as a fraction, corrected for phone clock skew. Ticks at `intervalMs`. Skew is
 * re-synced only when the server's sample changes, or the local tick would be cancelled out.
 */
function useRemaining(
  endsAt: number | null,
  serverNow: number | null,
  intervalMs: number,
): number | null {
  const [, tick] = useState(0);
  const skewRef = useRef(0);

  useEffect(() => {
    if (serverNow !== null) skewRef.current = serverNow - Date.now();
  }, [serverNow]);

  useEffect(() => {
    if (endsAt === null) return;
    const id = setInterval(() => tick((n) => n + 1), intervalMs);
    return () => clearInterval(id);
  }, [endsAt, intervalMs]);

  if (endsAt === null) return null;
  return Math.max(0, (endsAt - (Date.now() + skewRef.current)) / 1000);
}

export function Timer({
  endsAt,
  serverNow,
  variant = 'stage',
  lowAt = 5,
  decimals = 0,
  preciseUnder = 10,
}: TimerProps) {
  // A decimal clock ticks fast enough to see the hundredths move; a whole-second one does not need to.
  const remaining = useRemaining(endsAt, serverNow, decimals > 0 ? 50 : 200);
  if (remaining === null) return null;

  const showDecimals = decimals > 0 && remaining < preciseUnder;
  const label = showDecimals ? remaining.toFixed(decimals) : String(Math.ceil(remaining));
  const low = remaining <= lowAt;

  if (variant === 'chip') {
    return <span className="tag tag-live">{label}s</span>;
  }

  return (
    <div
      className={['tv-timer', variant === 'phone' ? 'is-phone' : '', low ? 'is-low' : '']
        .filter(Boolean)
        .join(' ')}
      role="timer"
      aria-label={`${label} seconds left`}
    >
      {label}
    </div>
  );
}
